/**
 * HxSwitch Component
 *
 * A switch (toggle) component for binary on/off settings following Moon Design System specifications with Angular
 * Material slide-toggle architecture.
 *
 * @feature 009-harmony-switch
 */

import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  inject,
  input,
  type InputSignal,
  model,
  OnDestroy,
  output,
  type OutputEmitterRef,
  type Signal,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ControlChange } from '../../base/control-change-event';
import { HxBaseControl } from '../../base/hx-base-control';
import { createIdSignal } from '../../base/id-manager';
import { HxControlSize } from '../../base/types';
import { HxSwitchEndIconDirective, HxSwitchStartIconDirective } from './switch-icon.directive';

/** Type definition for switch size variants */
export type SwitchSize = Extract<HxControlSize, '2xs' | 'xs' | 'sm'>;

@Component({
  selector: 'hx-switch',
  standalone: true,
  templateUrl: './switch.html',
  styleUrls: ['./switch.css', './switch-tokens.css', '../../styles/form-control-tokens.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hx-switch]': 'true',
    '[class.hx-switch--checked]': 'isChecked()',
    '[class.hx-switch--disabled]': 'disabledState()',
    '[class.hx-switch--2xs]': 'size() === "2xs"',
    '[class.hx-switch--xs]': 'size() === "xs"',
    '[class.hx-switch--sm]': 'size() === "sm"',
    '[class.hx-switch--focused]': 'isFocusedViaKeyboard()',
    '[attr.role]': '"switch"',
    '[attr.aria-checked]': 'isChecked()',
    '[attr.aria-disabled]': 'disabledState() || null',
    '[attr.aria-invalid]': 'error() || null',
    '[attr.aria-required]': 'required() || null',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[attr.aria-labelledby]': 'ariaLabelledby() || null',
    '[attr.aria-describedby]': 'ariaDescribedby() || null',
    '[attr.tabindex]': 'disabledState() ? -1 : tabIndex()',
  },
})
export class HxSwitch extends HxBaseControl implements ControlValueAccessor, AfterViewInit, OnDestroy {
  // === Dependencies ===
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject(ElementRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly switchNgControl = inject(NgControl, { optional: true, self: true });

  // === Inputs ===
  readonly computedId = createIdSignal(this.id, 'hx-switch-');
  readonly size: InputSignal<SwitchSize> = input<SwitchSize>('sm');
  readonly name: InputSignal<string | undefined> = input<string | undefined>(undefined);
  /** Whether the control is required */
  readonly required: InputSignal<boolean> = input<boolean>(false);
  override readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, {
    alias: 'aria-label',
  });

  // === Outputs ===
  readonly valueChange: OutputEmitterRef<ControlChange<boolean>> = output<ControlChange<boolean>>();

  // === State (model signal for two-way binding) ===
  readonly checked = model<boolean>(false);

  // === View Queries ===
  readonly inputElement: Signal<ElementRef<HTMLInputElement> | undefined> =
    viewChild<ElementRef<HTMLInputElement>>('input');

  // === Content Queries (Icon Directives) ===
  private readonly startIcon = contentChild(HxSwitchStartIconDirective);
  private readonly endIcon = contentChild(HxSwitchEndIconDirective);

  // === Protected State ===
  protected readonly isFocusedViaKeyboard = signal(false);
  protected readonly isChecked = computed(() => this.checked());

  // === Computed Values ===
  protected readonly ariaChecked = computed(() => (this.checked() ? 'true' : 'false'));

  // === ControlValueAccessor State ===
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: boolean) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  // === Effects ===
  constructor() {
    super();
    if (this.switchNgControl) {
      this.switchNgControl.valueAccessor = this;
    }

    // Monitor checked state and update icon visibility
    effect(() => {
      const checked = this.checked();
      const start = this.startIcon();
      const end = this.endIcon();

      if (start && end) {
        if (checked) {
          start.hide();
          end.show();
        } else {
          start.show();
          end.hide();
        }
      }
    });
  }

  // === Lifecycle Hooks ===
  ngAfterViewInit(): void {
    const inputEl = this.inputElement();
    if (inputEl) {
      this.focusMonitor.monitor(inputEl, true).subscribe((origin: FocusOrigin) => {
        this.isFocusedViaKeyboard.set(origin === 'keyboard');
      });
    }
    // Icon visibility is handled by effect automatically
  }

  ngOnDestroy(): void {
    const inputEl = this.inputElement();
    if (inputEl) {
      this.focusMonitor.stopMonitoring(inputEl);
    }
  }

  // === Event Handlers ===
  protected handleTrackClick(event: Event): void {
    // Prevent toggle if disabled (check combined state from input and CVA)
    if (this.disabledState()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Toggle the checked state
    const newValue = !this.checked();

    // Update checked state (effect will handle icon visibility)
    this.checked.set(newValue);
    this.changeDetectorRef.markForCheck();

    // Update the native input to stay in sync
    const inputEl = this.inputElement();
    if (inputEl) {
      inputEl.nativeElement.checked = newValue;
    }

    // Notify forms
    this.onChange(newValue);

    // Emit change event
    this.valueChange.emit(new ControlChange(this, newValue));
  }

  protected handleInputKeydown(event: KeyboardEvent): void {
    if (this.disabledState() || this.readonly()) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      target.click();
    }
  }

  protected handleInputChange(event: Event): void {
    // This handles keyboard interactions (Space/Enter on the input)
    if (this.disabledState()) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLInputElement;
    const newValue = target.checked;

    // Update checked state
    this.checked.set(newValue);
    this.changeDetectorRef.markForCheck();

    // Notify forms
    this.onChange(newValue);

    // Emit change event
    this.valueChange.emit(new ControlChange(this, newValue));
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  // === Public API ===
  /** Toggles the checked state of the switch. */
  toggle(): void {
    this.checked.set(!this.checked());
    this.onChange(this.checked());
  }

  // === ControlValueAccessor Implementation ===
  override writeValue(value: boolean): void {
    // Coerce null/undefined to false
    this.checked.set(value ?? false);
  }

  override registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  override setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }
}
